/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { DataApiService } from './data-api-service';
import { ContextualRequestService } from './contextual-request.service';
import { EntityInfoService } from '../metadata-api/entity-info.service';
import { RoutingService } from 'src/app/service/routing.service';
import { AuthService } from 'src/app/service/auth.service';
import { QueryApiService } from '../customer-api/query-api.service';
import { ConfigService } from 'src/app/service/config.service';
import { ParamsBuilder } from '../../utils/params-builder';
import { PortfolioApiService } from '../customer-api/portfolio-api.service';

@Injectable({
  providedIn: 'root',
})
export class SentimentService {
  constructor(
    private dataApi: DataApiService,
    private contextualRequest: ContextualRequestService,
    private entityInfoService: EntityInfoService,
    private routing: RoutingService,
    private auth: AuthService,
    private queryApi: QueryApiService,
    private config: ConfigService,
    private portfolioApi: PortfolioApiService,
  ) {}

  /**
   * Context-aware sentiment request. Routes to the correct endpoint based on
   * the current routing context (entity, portfolio, query, scanner).
   * Direct replacement for yukkApi.isentiment().
   */
  getSentiment(iparams: any, newsday: boolean): Observable<any> {
    const params = this.buildSentimentParams(iparams, newsday);

    // fromAlertsSetup → always use entity path regardless of routing context
    if (iparams.fromAlertsSetup) {
      return this.dataApi
        .get<any>(`${iparams.type}/${iparams.id}/sentiment`, params)
        .pipe(
          mergeMap((resArg) => {
            const res = JSON.parse(JSON.stringify(resArg));
            if (res.entity?.compound_key) {
              return this.entityInfoService
                .getEntitiesInfo(
                  [res.entity.compound_key],
                  'object',
                  iparams.getSanctions ? true : false,
                )
                .pipe(
                  map((entityInfo: any) => ({
                    entity: entityInfo[res.entity.compound_key],
                    sentiment: res.sentiment,
                  })),
                );
            }
            return of(res);
          }),
        );
    }

    // Widget context — route based on widget type (portfolio/query/entity)
    if (this.routing.isWidget()) {
      if (
        (this.routing.getPathSegment(2) === 'portfolio' ||
          iparams.type === 'portfolio') &&
        !iparams.portfolioId
      ) {
        return this.dataApi.post(
          'portfolio/sentiment',
          this.portfolioApi.activePortfolioContent,
          params,
        );
      } else if (this.routing.getPathSegment(2) === 'query') {
        return this.dataApi
          .post('search_query/sentiment', this.auth.query?.query, params)
          .pipe(catchError(() => of(undefined)));
      }
      // Default widget (entity, isin) falls through to entity context below
    }

    // iquery → resolve query then POST to search_query/sentiment
    if (iparams.iquery) {
      return this.queryApi
        .resolveQuery(iparams.iquery.uid)
        .pipe(
          switchMap((response: any) =>
            this.dataApi
              .post('search_query/sentiment', response.query, params)
              .pipe(catchError(() => of(undefined))),
          ),
        );
    }

    // All other contexts — delegate routing, then enrich entity if present
    return this.contextualRequest.request('sentiment', iparams, params).pipe(
      mergeMap((resArg) => {
        if (!resArg) return of(resArg);
        const res = JSON.parse(JSON.stringify(resArg));
        if (res.entity?.compound_key) {
          return this.entityInfoService
            .getEntitiesInfo(
              [res.entity.compound_key],
              'object',
              iparams.getSanctions ? true : false,
            )
            .pipe(
              map((entityInfo: any) => ({
                entity: entityInfo[res.entity.compound_key],
                sentiment: res.sentiment,
              })),
            );
        }
        return of(res);
      }),
    );
  }

  /**
   * Get sentiment for a single entity with optional entity info enrichment.
   * Replaces the entity path of yukkApi.isentiment().
   */
  getEntitySentiment(
    entityType: string,
    entityId: string,
    iparams: Record<string, any>,
    options?: { newsday?: boolean; getSanctions?: boolean },
  ): Observable<any> {
    const params = this.buildSentimentParams(iparams, options?.newsday);

    return this.dataApi
      .get<any>(`${entityType}/${entityId}/sentiment`, params)
      .pipe(
        mergeMap((resArg) => {
          const res = JSON.parse(JSON.stringify(resArg));
          if (res.entity?.compound_key) {
            return this.entityInfoService
              .getEntitiesInfo(
                [res.entity.compound_key],
                'object',
                options?.getSanctions ?? false,
              )
              .pipe(
                map((entityInfo: any) => ({
                  entity: entityInfo[res.entity.compound_key],
                  sentiment: res.sentiment,
                })),
              );
          }
          return of(res);
        }),
      );
  }

  /**
   * Get sentiment for a portfolio.
   * Replaces yukkApi.portfolioSentiment().
   */
  getPortfolioSentiment(
    portfolio: string[],
    iparams: Record<string, any>,
    newsday = false,
  ): Observable<any> {
    const params = this.buildSentimentParams(iparams, newsday);
    return this.dataApi.post('portfolio/sentiment', portfolio, params);
  }

  /**
   * Get sentiment broken down by entities in a portfolio.
   * Returns object keyed by compound_key with sentiment data.
   * Replaces yukkApi.sentimentItemized().
   */
  getSentimentItemized(
    portfolio: string[],
    iparams: Record<string, any>,
  ): Observable<Record<string, any>> {
    const params = this.buildSentimentParams(iparams, false);

    return this.dataApi
      .post<any>('portfolio/sentiment_itemized', portfolio, params, 'v2')
      .pipe(
        map((resp) => {
          if (
            !Object.prototype.hasOwnProperty.call(resp, 'sentiment_itemized')
          ) {
            return throwError(
              () => new Error('Error: wrong format of the response.'),
            );
          }

          const respCopy = JSON.parse(
            JSON.stringify(resp['sentiment_itemized']),
          );
          const entitiesData: Record<string, any> = {};
          portfolio.forEach((el) => {
            const filteredEntity = respCopy.filter(
              (item: any) => item.entity.compound_key === el,
            );
            if (filteredEntity.length > 0) {
              entitiesData[el] = filteredEntity[0].sentiment;
            }
          });
          return entitiesData;
        }),
      );
  }

  private buildSentimentParams(
    iparams: Record<string, any>,
    newsday?: boolean,
  ): Record<string, any> {
    if (!iparams) {
      return {};
    }
    const paramsToBeSent = ['period', ...ParamsBuilder.PARAMS_FOR_ALL_REQUESTS];
    // Caller may opt out of newsday narrowing (e.g. sentiment_active flow);
    // strip the field so ParamsBuilder's default URL `time` range applies.
    const sourceParams = newsday ? iparams : { ...iparams, newsday: undefined };
    return ParamsBuilder.build(
      sourceParams,
      paramsToBeSent,
      this.config.appConfig.routing.apiproj,
    );
  }
}
